import { Api } from '@/Api';
import { DEBUG_NAMESPACE, useDebug } from '@/composables/useDebug';
import { useToast } from '@/composables/useToast';
import { defineStore } from 'pinia';

// Rails enum string values ride through untouched by the snake_case<->camelCase
// key conversion (only object keys are converted, not string values).
export type ToDoSuggestedAction = 'general' | 'bug_report' | 'feature_request' | 'documentation' | 'email';
export type ToDoItemStatus = 'open' | 'done';
// Status of the background run that generates the list (an Enrichments::ToDoList).
export type ToDoGenerationStatus = 'enqueued' | 'running' | 'completed' | 'failed';

export interface ToDoItem {
  id: string;
  text: string;
  suggestedAction: ToDoSuggestedAction;
  status: ToDoItemStatus;
  videoTime: number | null;
  position: number;
}

export interface ToDoState {
  items: ToDoItem[];
  generationStatus: ToDoGenerationStatus | null;
}

const { debug } = useDebug(DEBUG_NAMESPACE.STORE_TO_DO);

// Pusher events can trigger overlapping fetches; only the latest one may write.
let latestRequest: ReturnType<typeof Api.item.toDoItems.list> | null = null;

export const useToDoStore = defineStore('toDoStore', {
  state: () =>
    ({
      items: [],
      generationStatus: null,
    }) as ToDoState,
  actions: {
    resetForNewItem() {
      this.$patch({ items: [], generationStatus: null });
    },

    async fetchToDoItems(itemId: string) {
      const request = Api.item.toDoItems.list(itemId);
      latestRequest = request;
      try {
        const response = await request;
        if (request !== latestRequest) {
          return;
        }
        const rawItems: Array<{ id: string; attributes: Omit<ToDoItem, 'id'> }> = response.data?.itemToDos ?? [];
        this.$patch({ items: rawItems.map(({ id, attributes }) => ({ id, ...attributes })), generationStatus: response.meta?.generationStatus ?? null });
      } catch (error) {
        debug(`Failed to fetch to-do items for item: ${itemId}`, error);
      }
    },

    patchToDoItem(data: { id: string; attributes: Partial<Omit<ToDoItem, 'id'>> }) {
      if (!data?.id || !data?.attributes) {
        return;
      }

      const index = this.items.findIndex(item => item.id === data.id);
      if (index === -1) {
        return;
      }
      this.items.splice(index, 1, { ...this.items[index], ...data.attributes });
    },

    async setStatus(itemId: string, id: string, status: ToDoItemStatus) {
      const previous = this.items.find(item => item.id === id);
      if (!previous) {
        return;
      }

      this.patchToDoItem({ id, attributes: { status } });

      try {
        await Api.item.toDoItems.update(itemId, id, status);
      } catch (error) {
        this.patchToDoItem({ id, attributes: { status: previous.status } });
        useToast().error("Couldn't save that change. Please try again.");
        debug(`Failed to update to-do item ${id} for item: ${itemId}`, error);
      }
    },
  },
  getters: {
    // Remaining (not yet done) items, so the tab badge clears as items are checked off.
    count: (state: ToDoState) => state.items.filter(item => item.status !== 'done').length,
    isGenerating: (state: ToDoState) => state.generationStatus === 'enqueued' || state.generationStatus === 'running',
    hasGenerationFailed: (state: ToDoState) => state.generationStatus === 'failed',
  },
});
